import React, { useMemo, useState } from 'react';

import { CreateOrEditSnippetCategoryModal } from '../../../features/snippets/ui/components';

import { DeleteSnippetCategoryConfirmationModal } from '../../../features/snippets/ui/components/delete-snippet-category';

import { SnippetCategoryWithSnippets } from '@distribute/shared/types';

import { SnippetsCategory } from './SnippetsCategory';

import { getSnippets, SORT_ITEMS } from '../lib';
import { NoSnippetsFound } from './NoSnippetsFound';

import { useSnippetsPermissions } from '../../../features/teams';
import { FILTER_SNIPPET_ITEMS } from '../../../entities/snippets';

type SnippetsTeamProps = {
  sort: keyof typeof SORT_ITEMS;
  filterByOwner: string;
  filter: keyof typeof FILTER_SNIPPET_ITEMS;
  isFiltersActive: boolean;
  categories: SnippetCategoryWithSnippets[];
};

export const SnippetsTeam: React.FC<SnippetsTeamProps> = ({
  categories,
  isFiltersActive,
  filter,
  filterByOwner,
  sort,
}) => {
  const [deletingCategory, setDeletingCategory] =
    useState<SnippetCategoryWithSnippets | null>(null);
  const [editingCategory, setEditingCategory] =
    useState<SnippetCategoryWithSnippets | null>(null);

  const { isCanManageCategories } = useSnippetsPermissions();

  const filteredCategories = useMemo(
    () =>
      categories.map((category) => ({
        ...category,
        snippets: getSnippets({
          snippets: category.snippets,
          filterByOwner,
          filter,
          sort,
        }),
      })),
    [categories, filterByOwner, filter, sort]
  );

  const count = useMemo(() => {
    return filteredCategories.reduce(
      (acc, category) => acc + category.snippets.length,
      0
    );
  }, [filteredCategories]);

  return (
    <>
      {deletingCategory && (
        <DeleteSnippetCategoryConfirmationModal
          isOpen={!!deletingCategory}
          onClose={() => setDeletingCategory(null)}
          category={deletingCategory}
        />
      )}
      {editingCategory && (
        <CreateOrEditSnippetCategoryModal
          isOpen={!!editingCategory}
          onClose={() => setEditingCategory(null)}
          category={editingCategory}
        />
      )}
      {isCanManageCategories ? (
        <SnippetsTeamManager
          isFiltersActive={isFiltersActive}
          count={count}
          categories={filteredCategories}
          setDeletingCategory={setDeletingCategory}
          setEditingCategory={setEditingCategory}
        />
      ) : (
        <SnippetsTeamMember
          count={count}
          categories={filteredCategories}
          setDeletingCategory={setDeletingCategory}
          setEditingCategory={setEditingCategory}
        />
      )}
    </>
  );
};

type SnippetsTeamManagerProps = {
  isFiltersActive: boolean;
  count: number;
  categories: SnippetCategoryWithSnippets[];
  setDeletingCategory: (category: SnippetCategoryWithSnippets) => void;
  setEditingCategory: (category: SnippetCategoryWithSnippets) => void;
};

const SnippetsTeamManager = ({
  isFiltersActive,
  count,
  categories,
  setDeletingCategory,
  setEditingCategory,
}: SnippetsTeamManagerProps) => {
  if (count === 0 && isFiltersActive) {
    return <NoSnippetsFound type="team" />;
  }

  if (categories.length === 0) {
    return <NoSnippetsFound type="team" />;
  }

  return (
    <>
      {categories.map((category) => {
        const hasSnippetsInCategory = category.snippets.length > 0;

        // For managers: render if it has snippets, OR if filters are not active (to show empty categories).
        const shouldRenderCategory = hasSnippetsInCategory || !isFiltersActive;

        return shouldRenderCategory ? (
          <SnippetsCategory
            key={category.id}
            category={category}
            onDeleteCategory={() => setDeletingCategory(category)}
            onEditCategory={() => setEditingCategory(category)}
          />
        ) : null;
      })}
    </>
  );
};

type SnippetsTeamMemberProps = {
  count: number;
  categories: SnippetCategoryWithSnippets[];
  setDeletingCategory: (category: SnippetCategoryWithSnippets) => void;
  setEditingCategory: (category: SnippetCategoryWithSnippets) => void;
};

const SnippetsTeamMember = ({
  count,
  categories,
  setDeletingCategory,
  setEditingCategory,
}: SnippetsTeamMemberProps) => {
  if (count === 0) {
    return <NoSnippetsFound type="team" />;
  }

  return (
    <>
      {categories.map((category) => {
        // For non-managers: only render if it has snippets.
        const hasSnippetsInCategory = category.snippets.length > 0;

        return hasSnippetsInCategory ? (
          <SnippetsCategory
            key={category.id}
            category={category}
            onDeleteCategory={() => setDeletingCategory(category)}
            onEditCategory={() => setEditingCategory(category)}
          />
        ) : null;
      })}
    </>
  );
};
